
/*
 * (c) 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 * 
 * This AWS Content is provided subject to the terms of the AWS Customer Agreement available at http://aws.amazon.com/agreement or other written agreement between Customer and either Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
 */

import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import React, { useEffect, useState } from 'react';
import axios from 'axios';

import './App.css';
import { Button, ContentLayout, Header, SpaceBetween } from "@cloudscape-design/components";
import { getAmplifyConfig } from './amplify-config';
import { getStageConfig } from './config';
import workbenchLogo from './assets/workbench_logo_white.png';


try {
  const amplifyConfig = getAmplifyConfig();
  Amplify.configure(amplifyConfig);
} catch (error) {
  console.error('Failed to configure Amplify:', error);
}


interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  error: string | null;
}

export default function App() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null
  });

  // Check authentication status on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: {
          ...user,
          id_token: session.tokens?.idToken?.toString(),
          access_token: session.tokens?.accessToken?.toString()
        },
        error: null
      });
    } catch (error) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
      });
    }
  };

  // HTTP interceptor to handle Access Token expiration
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            console.log('[HTTP Interceptor] 401 detected, refreshing session...');
            const session = await fetchAuthSession();
            originalRequest.headers.Authorization = `Bearer ${session.tokens?.accessToken?.toString()}`;
            return axios.request(originalRequest);
          } catch (refreshError) {
            console.error('[HTTP Interceptor] Session refresh failed:', refreshError);
            await signOut();
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => axios.interceptors.response.eject(responseInterceptor);
  }, []);

  const handleSignIn = async () => {
    try {
      await signInWithRedirect();
      await checkAuthState();
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
      });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const callApiGateway = async () => {
    try {
      const resource = 'projects'
      const config = getStageConfig();
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const url = `${config.piperBridgeGatewayUrl}/${resource}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      const data = await response.json();
      console.log('API Response:', data);
    } catch (error) {
      console.error('API call failed:', error);
    }
  };

  if (authState.isLoading) {
    return (
      <ContentLayout
        disableOverlap
        headerVariant="high-contrast"
        header={
          <SpaceBetween size="xl">
            <div style={{backgroundColor: '#232F3E', padding: '20px'}} >
            <Header
              variant="h1"
              description={ <span style={{ fontSize: '18px', color:'white'}}>
              Loading...</span>}>
              <img 
                src={workbenchLogo} 
                alt="Workbench Streams"
                style={{
                  height: '48px',
                  width: 'auto',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  padding: '5px', // Optional: adds some space around the image
                  borderRadius: '4px' // Optional: rounds the corners
                }}
              />
            </Header>
            </div>             
          </SpaceBetween>
        }>
      </ContentLayout>
    );
  }

  if (authState.error) {
    return <div>Encountering error... {authState.error}</div>;
  }


  return (          
    <ContentLayout
      disableOverlap
      headerVariant="high-contrast"
      header={
        <SpaceBetween size="xl">
          <div style={{backgroundColor: '#232F3E', padding: '20px'}} >
          <Header
            variant="h1"
            description={ <span style={{ fontSize: '18px', color:'white'}}>
            Sign in to view regions and start a Workbench stream.</span>}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={handleSignIn}>Sign In</Button>
                {authState.isAuthenticated && (<Button variant="primary" onClick={handleSignOut}>Sign Out</Button>)}
              </SpaceBetween>
            }>
            <img 
              src={workbenchLogo} 
              alt="Workbench Streams"
              style={{
                height: '48px',
                width: 'auto',
                display: 'inline-block',
                verticalAlign: 'middle',
                padding: '5px', // Optional: adds some space around the image
                borderRadius: '4px' // Optional: rounds the corners
              }}
            />
          </Header>
          </div>             
        </SpaceBetween>
      }>
      <div style={{ padding: '20px' }}>
        <strong>Authentication Status:</strong> <span style={{ color: authState.isAuthenticated ? 'green' : 'red' }}>
          {authState.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
        </span>
      {authState.isAuthenticated && (
        <div style={{ padding: '20px', marginBottom: '20px' }}>
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" onClick={callApiGateway}>
              Call API Gateway
            </Button>
            <Button onClick={handleSignOut}>Sign Out</Button>
          </SpaceBetween>
        </div>
      )}

      </div>
    </ContentLayout>
  );
}